import { CONNECTION_STATUS, formatParisDateTime, STORE_MODE } from '@flux/shared';
import { Plus } from 'lucide-react';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { ApiError } from '@/lib/api/client.js';

export interface StoreListProps {
  onSelect: (code: string) => void;
  onAddNew: () => void;
}

const SKELETON_ROWS = 3;

/**
 * Liste des boutiques — `02` §12.2. Réutilise `useStoresList()`
 * (`features/stores/use-stores-list.ts`, écrit par la vague des Boutiques
 * pour le sélecteur global) : même endpoint, même clé de requête, pas de
 * duplication de la requête liste.
 *
 * Panneau `flush` : le tableau touche les bords, l'action principale est dans
 * l'en-tête de la section.
 */
export function StoreList({ onSelect, onAddNew }: StoreListProps): JSX.Element {
  const query = useStoresList();

  return (
    <PageSection
      title="Boutiques Shopify"
      description="Sélectionnez une boutique pour ouvrir sa fiche."
      flush
      actions={
        <Button type="button" onClick={onAddNew}>
          <Plus aria-hidden="true" />
          Ajouter une boutique
        </Button>
      }
    >
      {query.isPending ? (
        <div aria-hidden="true" className="divide-y divide-border">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <div key={`skeleton-${String(index)}`} className="flex h-11 items-center gap-6 px-4">
              <Skeleton className="h-3.5 w-10" />
              <Skeleton className="h-3.5 w-48" />
              <Skeleton className="h-3.5 w-16" />
              <Skeleton className="h-3.5 w-20" />
            </div>
          ))}
        </div>
      ) : null}

      {query.isError ? (
        <ErrorState
          message={
            query.error instanceof ApiError
              ? query.error.message
              : 'Une erreur technique est survenue.'
          }
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
          className="rounded-none border-0 bg-transparent"
        />
      ) : null}

      {query.data?.items.length === 0 ? (
        <EmptyState
          title="Aucune boutique"
          description="Ajoutez une boutique pour démarrer sa configuration."
          action={
            <Button type="button" variant="outline" onClick={onAddNew}>
              Ajouter une boutique
            </Button>
          }
        />
      ) : null}

      {query.data && query.data.items.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Domaine myshopify</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>État de connexion</TableHead>
              <TableHead>Dernier webhook</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.data.items.map((store) => (
              <TableRow
                key={store.id}
                role="button"
                tabIndex={0}
                className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                onClick={() => {
                  onSelect(store.code);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(store.code);
                  }
                }}
              >
                <TableCell className="font-medium">{store.code}</TableCell>
                <TableCell className="text-muted-foreground">{store.shopDomain}</TableCell>
                <TableCell>
                  <StatusBadge registry={STORE_MODE} code={store.mode} />
                </TableCell>
                <TableCell>
                  <StatusBadge registry={CONNECTION_STATUS} code={store.connection.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground">
                  {store.lastWebhookAt ? formatParisDateTime(new Date(store.lastWebhookAt)) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </PageSection>
  );
}
